import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Bug, Lightbulb, Heart, MessageCircle, RefreshCcw, Loader2 } from "lucide-react";

const CATEGORY_META = {
  bug: { label: "Bug", icon: Bug, color: "bg-rose-50 text-rose-700 border-rose-200" },
  sugerencia: { label: "Sugerencia", icon: Lightbulb, color: "bg-amber-50 text-amber-700 border-amber-200" },
  elogio: { label: "Elogio", icon: Heart, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  otro: { label: "Otro", icon: MessageCircle, color: "bg-slate-50 text-slate-700 border-slate-200" },
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_category: { bug: 0, sugerencia: 0, elogio: 0, otro: 0 } });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { category: filter };
      const [listRes, statsRes] = await Promise.all([
        api.get("/feedback", { params: { ...params, limit: 200 } }),
        api.get("/feedback/stats"),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data || { total: 0, by_category: {} });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo cargar el feedback");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const summaryCards = useMemo(
    () => [
      { key: "total", label: "Total", value: stats.total, color: "text-[#0052FF]" },
      { key: "bug", label: "Bugs", value: stats.by_category?.bug ?? 0, color: "text-rose-600" },
      { key: "sugerencia", label: "Sugerencias", value: stats.by_category?.sugerencia ?? 0, color: "text-amber-600" },
      { key: "elogio", label: "Elogios", value: stats.by_category?.elogio ?? 0, color: "text-emerald-600" },
      { key: "otro", label: "Otros", value: stats.by_category?.otro ?? 0, color: "text-slate-600" },
    ],
    [stats]
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-feedback-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-semibold text-[#F1F5F9]">Feedback beta</h1>
            <p className="text-sm text-[#64748B] mt-1">Mensajes enviados por tus beta testers — solo administradores.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] bg-[#131C2E]" data-testid="admin-feedback-filter">
                <SelectValue placeholder="Filtrar por categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-all">Todas las categorías</SelectItem>
                <SelectItem value="bug" data-testid="filter-bug">Bugs</SelectItem>
                <SelectItem value="sugerencia" data-testid="filter-sugerencia">Sugerencias</SelectItem>
                <SelectItem value="elogio" data-testid="filter-elogio">Elogios</SelectItem>
                <SelectItem value="otro" data-testid="filter-otro">Otros</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="admin-feedback-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-feedback-stats">
          {summaryCards.map((s) => (
            <Card key={s.key} className="p-4 bg-[#131C2E] border-[#2A3447]" data-testid={`stat-${s.key}`}>
              <p className="text-xs uppercase tracking-wider text-[#94A3B8]">{s.label}</p>
              <p className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.value}</p>
            </Card>
          ))}
        </div>

        {/* List */}
        <div className="space-y-3" data-testid="admin-feedback-list">
          {loading && items.length === 0 ? (
            <Card className="p-8 flex items-center justify-center text-[#64748B] bg-[#131C2E]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </Card>
          ) : items.length === 0 ? (
            <Card className="p-8 text-center text-[#64748B] bg-[#131C2E]" data-testid="admin-feedback-empty">
              <p>Sin feedback {filter !== "all" ? `en categoría "${CATEGORY_META[filter]?.label}"` : "todavía"}.</p>
              <p className="text-xs mt-2">Cuando un beta tester envíe un mensaje, aparecerá aquí.</p>
            </Card>
          ) : (
            items.map((f) => {
              const meta = CATEGORY_META[f.category] || CATEGORY_META.otro;
              const Icon = meta.icon;
              return (
                <Card key={f.id} className="p-4 bg-[#131C2E] border-[#2A3447]" data-testid={`feedback-item-${f.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`${meta.color} border`}>{meta.label}</Badge>
                        <span className="text-sm font-medium text-[#F1F5F9]">{f.user_name || f.user_email}</span>
                        <span className="text-xs text-[#64748B]">·</span>
                        <span className="text-xs text-[#64748B]">{f.user_email}</span>
                        <span className="text-xs text-[#64748B] ml-auto">{formatDate(f.created_at)}</span>
                      </div>
                      <p className="text-sm text-[#F1F5F9] mt-2 whitespace-pre-wrap leading-relaxed">{f.content}</p>
                      {f.page_path && (
                        <p className="text-[11px] text-[#94A3B8] mt-2">Página: <code>{f.page_path}</code></p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
